/**
 * The Draft Planner: the extension's planner component, in a box of its own (a shadow root, so its styles and the
 * site's don't mix). With a linked Showdown name it knows your leagues and saves your drafts and matchups with
 * OgreClan, shared with /planner on Showdown; otherwise it saves them in this browser.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { api, signInUrl, type LeagueSummary, type LeagueView } from '../api';
import { useMe } from '../me';
import { appearance, watchAppearance } from '../theme';
import { toComponentState, toServerState, type ComponentState } from '../planner-state';
import { Link } from '../router';
import { ErrorBox, toast } from '../ui';

interface PlannerStore {
	get(key: string): Promise<unknown>;
	set(key: string, value: unknown): Promise<void>;
}
interface PlannerController {
	setTheme(dark: boolean): void;
	setScheme?(scheme: string | null): void;
}
declare global {
	interface Window {
		DRAFT_PLANNER_DATA?: unknown;
		DraftPlannerLib?: unknown;
		DraftPlannerPlans?: unknown;
		DraftPlannerUI?: { mount(container: Element, options: { store: PlannerStore, embedded?: boolean, liveLeagues?: boolean }): Promise<PlannerController> };
	}
}

function loadScript(src: string) {
	return new Promise<void>((resolve, reject) => {
		if (document.querySelector(`script[src="${src}"]`)) return resolve();
		const script = document.createElement('script');
		script.src = src;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error(`Couldn't load the planner (${src}).`));
		document.head.appendChild(script);
	});
}

/** A league as the planner component knows it: the shape of the draft pages' sync code */
function plannerLeague(view: LeagueView) {
	const { league } = view;
	return {
		v: 1, key: view.roomid, server: 'ogreclan', room: view.roomid, title: view.title, viewer: view.you || '',
		phase: league.phase, format: league.settings.format, type: league.settings.type, budget: league.settings.budget,
		picks: league.settings.picks, tera: league.settings.tera, week: league.week,
		players: league.players.map(p => ({ id: p.id, name: p.name, roster: p.roster, captains: p.captains })),
		pool: league.pool, schedule: league.schedule.map(week => week.map(match => [match.p1, match.p2])), syncedAt: Date.now(),
		mod: view.mod,
	};
}

/** Saves to this browser, for people who haven't linked a Showdown name */
const browserStore: PlannerStore = {
	async get(key) {
		try {
			return JSON.parse(localStorage.getItem(`draftplanner:${key}`) || 'null');
		} catch {
			return null;
		}
	},
	async set(key, value) {
		try {
			localStorage.setItem(`draftplanner:${key}`, JSON.stringify(value));
		} catch {}
	},
};

/** Saves to OgreClan, as /planner on Showdown does: your leagues come from the site */
async function accountStore(showdownId: string): Promise<{ store: PlannerStore, mod: string }> {
	const [{ data }, { leagues }] = await Promise.all([
		api.get<{ data: Record<string, unknown> | null }>('/api/planner'),
		api.get<{ leagues: LeagueSummary[] }>('/api/leagues'),
	]);
	const views = await Promise.all(leagues.filter(l => l.you).map(l => api.get<LeagueView>(`/api/leagues/${l.roomid}`)));
	const plannerLeagues = Object.fromEntries(views.map(view => [view.roomid, plannerLeague(view)]));
	// one set of Pokémon data for the planner: the one most of your leagues use
	const mods = views.map(view => view.mod);
	const mod = mods.sort((a, b) => mods.filter(m => m === b).length - mods.filter(m => m === a).length)[0] || 'gen9';
	let saved = data;
	let timer: ReturnType<typeof setTimeout> | null = null;
	return {
		mod,
		store: {
			async get(key) {
				if (key === 'leagues') return plannerLeagues;
				if (key === 'plannerState') return saved ? toComponentState(saved, showdownId) : null;
				return null;
			},
			async set(key, value) {
				if (key !== 'plannerState') return;
				saved = toServerState(value as ComponentState, saved);
				// a burst of clicks saves once
				if (timer) clearTimeout(timer);
				timer = setTimeout(() => {
					api.put('/api/planner', { data: saved }).catch((e: Error) => toast(`Your plan couldn't be saved: ${e.message}`, 'bad'));
				}, 800);
			},
		},
	};
}

export function PlanPage() {
	const me = useMe();
	const host = useRef<HTMLDivElement>(null);
	const [error, setError] = useState<unknown>(null);
	const showdownId = me?.user?.showdown?.id;

	useEffect(() => {
		if (!me || !host.current) return;
		let cancelled = false;
		let stopWatching = () => {};
		const element = host.current;
		void (async () => {
			try {
				const { store, mod, live } = showdownId ? { ...await accountStore(showdownId), live: true } : { store: browserStore, mod: 'gen9', live: false };
				const snapshot = await fetch(`/data/${mod}.json`).then(r => r.ok ? r.json() : fetch('/data/gen9.json').then(r2 => r2.json()));
				window.DRAFT_PLANNER_DATA = snapshot;
				await loadScript('/planner/lib.js');
				await loadScript('/planner/plans.js');
				await loadScript('/planner/planner-ui.js');
				if (cancelled) return;
				const shadow = element.shadowRoot || element.attachShadow({ mode: 'open' });
				shadow.innerHTML = `<link rel="stylesheet" href="/planner/planner.css" /><div class="planner"></div>`;
				const controller = await window.DraftPlannerUI!.mount(shadow.querySelector('.planner')!, { store, liveLeagues: live });
				// the site's colour scheme
				const show = ({ dark, scheme }: ReturnType<typeof appearance>) => {
					controller.setTheme(dark);
					controller.setScheme?.(scheme);
				};
				show(appearance());
				stopWatching = watchAppearance(show);
			} catch (e) {
				if (!cancelled) setError(e);
			}
		})();
		return () => {
			cancelled = true;
			stopWatching();
		};
	}, [me === null, showdownId]);

	return <>
		{me && !me.user && <p class="notice">Your plans are saved in this browser. <a href={signInUrl()}>Sign in</a> and link your
			Showdown name to keep them with your account, the same ones as <code>/planner</code> on Showdown.</p>}
		{me?.user && !me.user.showdown && <p class="notice">Your plans are saved in this browser for now. <Link href="/account">Link
			your Showdown name</Link> to keep them with your account and see your leagues.</p>}
		{error ? <ErrorBox error={error} /> : null}
		<div ref={host} class="planner-host" key={showdownId || 'browser'} />
	</>;
}
